import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { emitOnce } from '@ngneat/elf';
import { useObservable } from '@ngneat/react-rxjs';
import { auditsInformationService } from '@store/audits/audits-information/audit-information.service';
import { RepositionDTO } from '@store/audits/audits-information/audits-information.model';
import { auditsOperationQuery } from '@store/audits/audits-operation/audit-operation.query';
import { AuditOperation, AuditOperationStepTypeEnum } from '@store/audits/audits-operation/audits-operation.model';
import { auditsOperationService } from '@store/audits/audits-operation/audits-operation.service';
import { sortByPosition } from '@utils/sortData';
import { useResponsive } from '@utils/useResponsive';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { finalize } from 'rxjs';
import PrecoContainerContent from '../../components/PrecoContainerContent';
import { useDragnDrop } from './utils/useDragnDrop';


interface PrecoTabProps {
  isTemplate?: boolean;
}

const PrecoTab = (props: PrecoTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auditId } = useParams();
  const { isTemplate } = props;
  const [isloading, setIsloading] = useState<boolean>(false);
  const [isDiseabled, setIsDiseabled] = useState<boolean>(true);
  const [dragnDropItems, setDragnDropItems] = useState<AuditOperation[]>([]);
  const { onDragEnd, getListStyle } = useDragnDrop();
  const { isTablets } = useResponsive();
  const [filters] = useObservable(auditsOperationQuery.filters$);
  const { enqueueSnackbar } = useSnackbar();


  const [{ auditsOperation }] = useObservable(auditsOperationQuery.auditsOperationList$);


  useEffect(() => {
    emitOnce(() => {
      auditsOperationService.setStep(AuditOperationStepTypeEnum.PRECO);
    });
    if (auditId) {
      auditsOperationService.getAuditsInformationOP(auditId).pipe(
        finalize(() => setTimeout(() => setIsloading(false), 100))
      ).subscribe();
    }

    return () => {
      auditsOperationService.resetStore();
    };
  }, [auditId, filters]);

  useEffect(() => {
    if (auditsOperation) setDragnDropItems(auditsOperation.sort(sortByPosition));
  }, [auditsOperation]);

  const handleSavePosition = () => {
    if (!isDiseabled && auditId) {
      const positionData: RepositionDTO = {
        data: dragnDropItems.map((item, idx) => ({ ressourceId: item.id, position: idx, isOperation: true })),
      };
      auditsInformationService.updatePosition(positionData, auditId).subscribe();
    }
    setIsDiseabled(!isDiseabled);
  };

  const handleExportCSV = () => {
    auditsOperationService.getPrecoAuditCSV(auditId!)
      .pipe()
      .subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  return (
    <Stack flex={1} width="100%" py={'20px'} spacing={2} sx={{ backgroundColor: 'white' }} mt='20px' borderRadius='2px' mb='20px'>
      <Stack direction="row" spacing={2} alignItems='center' justifyContent='space-between' px={'20px'}>
        <Typography fontSize="20px" fontWeight="700">
          {t('audits.global.preco')}
        </Typography>
        {!isTemplate && (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="primary" onClick={handleExportCSV}>
              {t('global.exportCSV')}
            </Button>
            <Button variant="outlined" color="primary" onClick={handleSavePosition}>
              {isDiseabled ? t('audits.buttons.dragnDrop') : t('audits.buttons.save-position')}
            </Button>
            <Button variant="contained" color="primary" onClick={() => { navigate('add-container'); }}>
              {t('audits.buttons.add-container')}
            </Button>
          </Stack>
        )}
      </Stack>
      <DragDropContext onDragEnd={(res) => onDragEnd(res, dragnDropItems, setDragnDropItems)}>
        <Stack overflow="auto" height={'15rem'} py={'15px'} px="20px" flexGrow={1} my="8px" spacing={4} className="scrollable">
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <Stack
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
                sx={{ width: isTablets ? 'fit-content' : undefined }}
                spacing={2}
              >
                {isloading ?
                  <CircularProgress
                    size={20}
                    style={{ alignSelf: 'center' }}
                    thickness={10}
                  />
                  : !isTemplate && dragnDropItems ? (
                    dragnDropItems.map((auditOperation, idx) =>
                      <Draggable
                        key={auditOperation.id.toString()}
                        draggableId={auditOperation.id.toString()}
                        isDragDisabled={isDiseabled}
                        index={idx}
                      >
                        {(provided) => (
                          <Stack
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              border: !isDiseabled ? '1px dashed #ccc' : 'none',
                            }}
                          >
                            <PrecoContainerContent key={idx} auditOperation={auditOperation} isDraggable={isDiseabled} />
                          </Stack>
                        )}
                      </Draggable>
                    )
                  ) : (
                    <Stack justifyContent='center' alignItems='center' flex={1}>
                      <Typography color='#868F9C' fontSize="15px" fontWeight="100">
                        {t('audits.global.preco-no-data')}
                      </Typography>
                    </Stack>
                  )}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </Stack>
      </DragDropContext>
      <Outlet />
    </Stack>
  );
};

export default PrecoTab;
